<template>
  <canvas
    ref="canvas"
    class="waveform-canvas"
    :class="{ 'waveform-loaded': peaks !== null }"
  />
</template>

<script>
import WaveformService from '@/services/WaveformService';

export default {
  name: 'WaveformCanvas',

  props: {
    trackName: {
      type: String,
      default: '',
    },
    // Track duration as displayed ("MM:SS" or "HH:MM:SS"). Lets the service
    // skip decoding very long tracks on memory-constrained iOS devices.
    duration: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      peaks: null,
      resizeObserver: null,
    };
  },

  watch: {
    trackName: {
      immediate: true,
      handler() {
        this.loadWaveform();
      },
    },
    '$vuetify.theme.dark'() {
      this.draw();
    },
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.alignAndDraw();
    });
    if (this.$refs.canvas && this.$refs.canvas.parentElement) {
      this.resizeObserver.observe(this.$refs.canvas.parentElement);
    }
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    WaveformService.cancelPending();
  },

  methods: {
    async loadWaveform() {
      this.peaks = null;
      if (!this.trackName) return;

      const requestedTrack = this.trackName;
      const peaks = await WaveformService.getPeaks(
        this.trackName,
        500,
        this.parseDurationSeconds(this.duration)
      );

      // Discard result if track changed during fetch
      if (this.trackName !== requestedTrack) return;

      if (peaks) {
        this.peaks = peaks;
        this.$nextTick(() => this.alignAndDraw());
      }
    },

    // "MM:SS" / "HH:MM:SS" -> seconds (null when unknown/malformed)
    parseDurationSeconds(duration) {
      if (!duration || typeof duration !== 'string') return null;
      const parts = duration.split(':').map((p) => parseInt(p, 10));
      if (parts.some((p) => Number.isNaN(p))) return null;
      return parts.reduce((total, part) => total * 60 + part, 0);
    },

    alignAndDraw() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const parent = canvas.parentElement;
      if (!parent) return;

      // Find the sibling v-slider's track container
      const trackContainer = parent.querySelector('.v-slider__track-container');
      if (trackContainer) {
        const parentRect = parent.getBoundingClientRect();
        const trackRect = trackContainer.getBoundingClientRect();
        canvas.style.left = (trackRect.left - parentRect.left) + 'px';
        canvas.style.width = trackRect.width + 'px';
      }

      // Size the canvas buffer to match its display size
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      this.draw();
    },

    draw() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.peaks) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      const peaks = this.peaks;
      const barCount = peaks.length;

      ctx.clearRect(0, 0, width, height);

      const isDark = this.$vuetify.theme.dark;
      ctx.fillStyle = isDark
        ? 'rgba(255, 255, 255, 0.16)'
        : 'rgba(0, 0, 0, 0.11)';

      const slotWidth = width / barCount;
      const barWidth = 1; // 1 canvas pixel — hairline
      const halfHeight = height / 2;

      // Draw as hairline vertical bars with tight spacing
      // Apply power curve (^1.8) to exaggerate dynamic range
      for (let i = 0; i < barCount; i++) {
        const exaggerated = Math.pow(peaks[i], 1.8);
        const peakHeight = exaggerated * halfHeight * 0.9;
        if (peakHeight < 0.5) continue;
        const x = Math.round(i * slotWidth + (slotWidth - barWidth) / 2);
        const y = Math.round(halfHeight - peakHeight);
        const h = Math.round(peakHeight * 2);
        ctx.fillRect(x, y, barWidth, h);
      }
    },
  },
};
</script>

<style scoped>
.waveform-canvas {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 43px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.waveform-loaded {
  opacity: 1;
}
</style>
